import { PresenceType } from '@momentum-ui/react-collaboration';
import { t } from 'i18next';

/**
 * i18n keys for user-visible presence status labels (see en/translation.json, namespace `translation`).
 * Use with {@link getPresenceStatusLabel} so strings stay centralized and localizable.
 */
export enum PresenceLabelKey {
  Active = 'presence.status.active',
  Away = 'presence.status.away',
  Busy = 'presence.status.busy',
  DoNotDisturb = 'presence.status.doNotDisturb',
  InACall = 'presence.status.inACall',
  InAMeeting = 'presence.status.inAMeeting',
  OutOfOffice = 'presence.status.outOfOffice',
  Presenting = 'presence.status.presenting',
  QuietHours = 'presence.status.quietHours',
}

const PRESENCE_LABEL_DEFAULT_EN: Record<PresenceLabelKey, string> = {
  [PresenceLabelKey.Active]: 'Active',
  [PresenceLabelKey.Away]: 'Away',
  [PresenceLabelKey.Busy]: 'Busy',
  [PresenceLabelKey.DoNotDisturb]: 'Do not disturb',
  [PresenceLabelKey.InACall]: 'In a call',
  [PresenceLabelKey.InAMeeting]: 'In a meeting',
  [PresenceLabelKey.OutOfOffice]: 'Out of office',
  [PresenceLabelKey.Presenting]: 'Presenting',
  [PresenceLabelKey.QuietHours]: 'Quiet hours',
};

const TRANSLATION_NS = 'translation';

/**
 * Localized display string for a presence label key (falls back to English if catalog not ready).
 */
export function getPresenceStatusLabel(key: PresenceLabelKey): string {
  return t(key, {
    ns: TRANSLATION_NS,
    defaultValue: PRESENCE_LABEL_DEFAULT_EN[key],
  });
}

/**
 * Presence status constants
 */
export const PRESENCE_STATUS = {
  // Unknowns
  UNKNOWN: 'unknown',
  PENDING: 'pending',

  // Idles
  INACTIVE: 'inactive',

  // Actives
  ACTIVE: 'active',

  // Busys
  DND: 'dnd',
  DO_NOT_DISTURB: 'DoNotDisturb',
  CALL: 'call',
  MEETING: 'meeting',
  PRESENTING: 'presenting',
  CALENDAR_ITEM: 'calendarItem',
  ON_HOLD: 'onHold',

  // Device
  ON_MOBILE: 'onMobile',
  ON_DEVICE: 'onDevice',

  // Aways
  OOO: 'ooo',
  OUT_OF_OFFICE: 'OutOfOffice',
  QUIET_HOURS: 'quietHours',
} as const;

/**
 * Presence status types from Webex SDK
 * Maps to Momentum UI PresenceType enum values
 */
export type PresenceStatus = 'active' | 'call' | 'meeting' | 'presenting' | 'inactive' | 'outOfOffice' | 'dnd' | 'DoNotDisturb' | 'calendarItem' | 'schedule' | 'quietHours' | 'onMobile' | 'onDevice' | 'onHold' | 'ooo' | 'unknown' | 'pending';
export type PresenceCategory = 'busy' | 'available' | 'idle' | 'active' | 'away' | null;

/**
 * Presence data structure from Webex SDK
 */
export interface PresenceData {
  status: PresenceStatus;
  category: PresenceCategory;
  customStatusMessage?: string;
  /**
   * Mercury may send `status: "meeting"` with `meetingType: "calendarItem"`.
   * We still surface calendar meetings as **In a call** to align with Teams (no calendar-meeting presence there)
   * and `msft-presence-service`.
   */
  meetingType?: string;
}

// Re-export Momentum UI's PresenceType for convenience
export { PresenceType };

/**
 * Converts Webex SDK presence data to UI-compatible presence type and label
 * 
 * @param _presence - Presence status string from SDK (e.g., 'active', 'call', 'meeting', etc.)
 * @param category - Optional presence category from SDK ('busy', 'available', etc.)
 * @param meetingType - When status is `meeting`, e.g. `calendarItem` (still mapped to **In a call** for Teams parity)
 * @returns Object with presenceType and presenceLabel for Avatar component
 */
export function convertPresenceType(
  _presence?: PresenceStatus | string,
  category?: PresenceCategory,
  meetingType?: string
): { presenceType: PresenceType; presenceLabel: string } {
  // Handle undefined/null inputs
  if (!_presence) {
    return {
      presenceType: PresenceType.Default,
      presenceLabel: '',
    };
  }

  // Normalize status to handle case variations
  const normalizedStatus = typeof _presence === 'string' ? _presence : String(_presence);
  
  // Map status to Momentum UI PresenceType enum
  switch (normalizedStatus) {
    case PRESENCE_STATUS.ACTIVE:
      // Active status can be combined with category
      if (category === 'busy') {
        return {
          presenceType: PresenceType.Busy,
          presenceLabel: getPresenceStatusLabel(PresenceLabelKey.Busy),
        };
      }
      return {
        presenceType: PresenceType.Active,
        presenceLabel: getPresenceStatusLabel(PresenceLabelKey.Active),
      };

    case PRESENCE_STATUS.CALL:
      return {
        presenceType: PresenceType.Call,
        presenceLabel: getPresenceStatusLabel(PresenceLabelKey.InACall),
      };

    case PRESENCE_STATUS.INACTIVE:
      return {
        presenceType: PresenceType.Away,
        presenceLabel: getPresenceStatusLabel(PresenceLabelKey.Away),
      };

    case PRESENCE_STATUS.OUT_OF_OFFICE:
    case PRESENCE_STATUS.OOO:
      return {
        presenceType: PresenceType.OOO,
        presenceLabel: getPresenceStatusLabel(PresenceLabelKey.OutOfOffice),
      };

    case PRESENCE_STATUS.MEETING:
      if (meetingType === PRESENCE_STATUS.CALENDAR_ITEM) {
        return {
          presenceType: PresenceType.Call,
          presenceLabel: getPresenceStatusLabel(PresenceLabelKey.InACall),
        };
      }
      return {
        presenceType: PresenceType.Meet,
        presenceLabel: getPresenceStatusLabel(PresenceLabelKey.InAMeeting),
      };

    case PRESENCE_STATUS.DND:
    case PRESENCE_STATUS.DO_NOT_DISTURB:
      return {
        presenceType: PresenceType.DND,
        presenceLabel: getPresenceStatusLabel(PresenceLabelKey.DoNotDisturb),
      };

    case PRESENCE_STATUS.QUIET_HOURS:
      return {
        presenceType: PresenceType.QuietHours,
        presenceLabel: getPresenceStatusLabel(PresenceLabelKey.QuietHours),
      };

    case PRESENCE_STATUS.PRESENTING:
      return {
        presenceType: PresenceType.Presenting,
        presenceLabel: getPresenceStatusLabel(PresenceLabelKey.Presenting),
      };

    case PRESENCE_STATUS.CALENDAR_ITEM:
    case 'schedule':
      return {
        presenceType: PresenceType.Call,
        presenceLabel: getPresenceStatusLabel(PresenceLabelKey.InACall),
      };

    default:
      return {
        presenceType: PresenceType.Default,
        presenceLabel: '',
      };
  }
}

